import { Component, inject } from '@angular/core';
import { ComponentsModule, DialogService, InputOption, ResultDataSource, SnackBarService } from '@mastechnicaegroup/carelinelive-ui';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { addParticipantFormGroup, AddParticipantData, StoreAddParticipantRequest, ChangeParticipantPresenceResponse } from '@lib/api/models/message';
import { SendableRequest } from '@mastechnicaegroup/carelinelive-cdk/requests';
import { MessageService } from '@lib/messages/message.service';
import { ResultObservable, toResult } from '@mastechnicaegroup/carelinelive-cdk/rx';
import { User } from '@lib/api/models/user';
import { UserService } from '@lib/user/user.service';
import { concatMap, map, shareReplay } from 'rxjs';

@Component({
    selector: 'app-add-participant-dialog',
    imports: [
        ComponentsModule,
        FormsModule,
        ReactiveFormsModule
    ],
    templateUrl: './add-participant-dialog.component.html',
    standalone: true,
    styleUrl: './add-participant-dialog.component.scss'
})
export class AddParticipantDialogComponent {

    protected readonly dialogRef = inject<DialogRef>(DialogRef);
    protected readonly data = inject<AddParticipantData>(DIALOG_DATA);
    protected readonly participantFormGroup = addParticipantFormGroup();
    private readonly _messageService = inject(MessageService);
    private readonly _snackBar = inject(SnackBarService);
    private readonly _userService = inject(UserService);

    protected readonly users$: ResultObservable<InputOption<User>[]> = this._userService.getAllPossibleThreadUsers(this.data.threadId).pipe(
        map((response): InputOption<User>[] => {
            return (response?.data ?? []).map(user => ({
                value: user,
                label: `${user.person.given_name} ${user.person.family_name}`,
                testId: user.id
            }));
        }),
        toResult(),
        shareReplay(1)
    );

    protected searchableUsersDataSource = new ResultDataSource<User>({
        source: this.users$,
        searchable: true,
        compareValue: (a, b) => a.id === b.id
    });

    addParticipantRequest = new SendableRequest<ChangeParticipantPresenceResponse, StoreAddParticipantRequest>({
        request: (payload) => {
            return this._messageService.addParticipant(payload!).pipe(
                concatMap(() =>
                    this._messageService.addRemoveParticipant({ ...payload!, removed: false })
                )
            )
        },

        onSend: () => {
            this.participantFormGroup.disable();
        },

        onSuccess: () => {
            this.participantFormGroup.enable();
            this._snackBar.open({
                colour: 'success',
                icon: 'check',
                title: 'Participant added'
            });
            this.dialogRef.close();
        },

        onError: () => {
            this.participantFormGroup.enable();
        }
    });

    addParticipant() {
        if (this.addParticipantRequest.loading()) {
            return;
        }

        const participant = this.participantFormGroup.controls.participant_id.value as { id: string } | null;

        if (!participant) {
            return;
        }

        const addParticipantData: StoreAddParticipantRequest = {
            thread_id: this.data.threadId,
            participant_id: participant.id || ''
        };
        this.addParticipantRequest.send(addParticipantData);
    }


    public static show(dialog: DialogService, data: AddParticipantData) {
        return dialog.openDialog<DialogRef, AddParticipantData>(AddParticipantDialogComponent, {
            data
        });
    }


}
